import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { APP_SHELL_CONFIG } from './app-shell.config';

export type ThemeMode = 'light' | 'dark';

// libs/client/web-preflight/inject.mjs 의 다크모드 pre-bootstrap 스크립트와 같은 키를
// 쓴다 — 둘 중 하나만 바꾸면 첫 페인트(pre-bootstrap)와 부트스트랩 이후(ThemeService)의
// 판정이 어긋나 깜빡임이 생긴다.
const STORAGE_KEY = 'sbe-theme-mode';
const DARK_CLASS = 'dark';
const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * `<html>`에 `.dark` 클래스를 토글해 Material(`--mat-sys-*`)과 Tailwind(`dark:`)를 같은
 * 신호로 동시에 움직인다 — libs/client/styles/material.scss·tailwind.css 참고. 선택은
 * localStorage에 저장하고, 명시적으로 저장된 값이 없으면 OS `prefers-color-scheme`을
 * 계속 실시간으로 따라간다(AppShellResponsiveService와 동일하게 반응형으로 추적).
 *
 * `APP_SHELL_CONFIG.themeIgnoresStoredRoutePrefixes`에 걸리는 경로(예: OIDC
 * authorize/consent 화면, RP가 window.open()으로 여는 팝업)는 localStorage를 아예
 * 안 읽고 OS 설정만 따른다 — 이런 화면은 cross-origin으로 전혀 다른 컨텍스트(제3자
 * 사이트가 연 팝업 등)에서 열릴 수 있는데, 이 origin에 과거 저장된(그 방문과 무관한)
 * 명시적 선택을 그대로 노출하면 호스트 페이지 테마와 불일치해 보일 수 있다. OS
 * 설정은 이런 문맥과 무관한 환경 신호라 상대적으로 안전하다.
 *
 * localStorage 접근은 전부 try/catch로 감싼다 — 이 워크스페이스의 web-preflight
 * 가드(libs/client/web-preflight/inject.mjs)가 존재하는 이유 자체가 프라이빗
 * 브라우징 등에서 localStorage 접근이 throw할 수 있어서고, 그 가드는 CSS로
 * `app-root`를 숨길 뿐 Angular 부트스트랩이나 이 서비스 생성 자체를 막지는 않는다 —
 * 여기서 막지 않으면 그 사용자군에서 셸 전체가 렌더되지 않는다.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly config = inject(APP_SHELL_CONFIG, { optional: true }) ?? {};

  readonly mode = signal<ThemeMode>(this.resolveInitialMode());

  constructor() {
    this.applyMode(this.mode());
    this.watchSystemPreference();
  }

  toggle(): void {
    this.setMode(this.mode() === 'dark' ? 'light' : 'dark');
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    this.applyMode(mode);
    this.writeStoredMode(mode);
  }

  private applyMode(mode: ThemeMode): void {
    this.document.documentElement.classList.toggle(DARK_CLASS, mode === 'dark');
  }

  private resolveInitialMode(): ThemeMode {
    if (!this.ignoresStoredMode()) {
      const stored = this.readStoredMode();
      if (stored) return stored;
    }
    return this.matchesPrefersDark() ? 'dark' : 'light';
  }

  /** 현재 경로가 `themeIgnoresStoredRoutePrefixes`에 걸리면 저장된 선택을 건너뛴다. */
  private ignoresStoredMode(): boolean {
    const prefixes = this.config.themeIgnoresStoredRoutePrefixes;
    if (!prefixes?.length) return false;
    const pathname = this.document.defaultView?.location?.pathname ?? '';
    return prefixes.some((prefix) => pathname.startsWith(prefix));
  }

  /** 사용자가 명시적으로 고른 적이 없는 동안(또는 이 경로가 저장값을 안 보는 동안)은 OS 설정 변경(예: 야간 자동 전환)을 계속 따라간다. */
  private watchSystemPreference(): void {
    const mql = this.document.defaultView?.matchMedia?.(PREFERS_DARK_QUERY);
    if (!mql) return;

    const onChange = (event: MediaQueryListEvent): void => {
      if (!this.ignoresStoredMode() && this.readStoredMode()) return; // 명시적으로 고른 값이 있으면 OS 변경을 무시한다.
      this.mode.set(event.matches ? 'dark' : 'light');
      this.applyMode(this.mode());
    };
    mql.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => mql.removeEventListener('change', onChange));
  }

  private matchesPrefersDark(): boolean {
    try {
      return this.document.defaultView?.matchMedia?.(PREFERS_DARK_QUERY)?.matches ?? false;
    } catch {
      return false;
    }
  }

  private readStoredMode(): ThemeMode | null {
    try {
      const stored = this.document.defaultView?.localStorage?.getItem(STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : null;
    } catch {
      return null;
    }
  }

  private writeStoredMode(mode: ThemeMode): void {
    try {
      this.document.defaultView?.localStorage?.setItem(STORAGE_KEY, mode);
    } catch {
      // 프라이빗 브라우징 등에서 저장을 못 해도 현재 세션의 토글 자체는 정상 동작해야 하므로 무시한다.
    }
  }
}
